/**
 * useIdleTimer (Plan 01-13, Task 1; issue #29 idle holds).
 *
 * Mounts the 60s idle watcher for `<DashboardRoute />`:
 *
 *   • On mount → schedule a 60_000ms timer that calls `goIdle()`.
 *   • On any pointerdown / touchstart / keydown / click → reset the timer.
 *   • mousemove is throttled to 1/s — some touchscreens emit dozens of
 *     synthetic mousemoves per swipe; without throttling we'd burn CPU
 *     calling clearTimeout + setTimeout every frame.
 *   • On document.visibilityState === 'hidden' → pause (clear the timer,
 *     don't fire).  On 'visible' → re-schedule from zero.
 *   • Unmount → remove every listener, drop the store subscription and clear
 *     the pending timer.
 *
 * 60_000ms matches DASH-02 (UI-SPEC §Interaction Contracts §1).  The hook
 * accepts an override for tests that need a shorter window, but the call
 * site in `<DashboardRoute />` uses the default.
 *
 * v1.1: the auto-idle timer only runs when `enabled` is true — i.e. on a
 * device the user has switched into wall-tablet mode.  Everywhere else the
 * app stays on the interactive surface and never auto-fades to the ambient
 * hub.  When `enabled` flips, the effect tears down / re-arms cleanly.
 *
 * The hook reads `useDashboard.getState()` inside the timer callback so it
 * always sees the current `goIdle` reference — even if the store were
 * recreated during HMR, the timer would dispatch to the live instance.
 *
 * ---------------------------------------------------------------------------
 * Issue #29 — idle holds
 *
 * `<DashboardRoute />` is the persistent app shell and `<AssistantRoute />` is
 * a child route under it, so this timer is armed while a Claude reply streams.
 * Before #29 the screen faded to the ambient hub mid-answer.  Anything on
 * screen that is still worth reading now takes a hold on the register in
 * `DashboardStore` (via `useIdleHold`), and this timer DEFERS while
 * `idleHolds > 0`.
 *
 * Deferring, never disabling.  Two mechanisms keep the deferral honest:
 *
 *   1. A short `HOLD_RECHECK_MS` re-arm on every held fire, so the fire path
 *      always has a next tick.  There is no branch here that leaves `timer`
 *      null with nothing to wake it — that would be an idle timer that
 *      silently stopped existing.
 *   2. A store subscription that restarts a FULL `idleMs` countdown the
 *      moment the count drops to zero.  Without it, the user would get
 *      whatever fraction of the re-check interval happened to be left after
 *      the last holder released — the 60s window is a promise about how long
 *      the screen stays lit after you stop interacting, so it restarts whole.
 *
 * MAX_HOLD_MS is the deadman ceiling, and the most important line in this
 * file.  A hold is a request, not a veto.  A wedged `'streaming'` status — a
 * fetch that neither resolves nor aborts, an SSE stream the server never
 * closes — must cost five minutes of lit screen, never a permanently dead
 * ambient hub in a dark kitchen, so the ceiling fires `goIdle()` regardless
 * of the hold count.
 *
 * Read the ceiling as *"a hold may keep the screen lit for at most five
 * minutes past the last genuine touch"*, not "past the moment the hold was
 * taken".  It is a wedged-stream mitigation and nothing more: the signal that
 * a stream is NOT wedged is the user still interacting with it, and that
 * signal has to reset the ceiling as well as the 60s window.  See MAX_HOLD_MS
 * below for what happens if it doesn't.
 */
import { useEffect } from 'react';
import { useDashboard } from './DashboardStore';

const IDLE_MS = 60_000;
const MOUSEMOVE_THROTTLE_MS = 1_000;

/**
 * How often the fire path re-checks while a hold is live.  Only the deadman
 * ceiling depends on this cadence — the release edge is handled immediately
 * by the store subscription — so it is tuned for "cheap", not "prompt".
 *
 * Exported for tests, which need the poll granularity to reason about how
 * long past the ceiling a fire may take.
 */
export const HOLD_RECHECK_MS = 10_000;

/**
 * Deadman ceiling: the longest a hold may keep the screen lit PAST THE LAST
 * GENUINE TOUCH.  Measured from `max(idleHeldSince, lastActivityAt)` — the
 * later of the 0→1 hold edge and the most recent real interaction.
 *
 * Two properties this baseline has to satisfy at once:
 *
 *   - Overlapping holders must not walk the ceiling forward, which is why the
 *     hold side of the max is the 0→1 edge and not "since the last holder
 *     joined" — that is `idleHeldSince`'s whole job (see `holdIdle`).
 *   - A hold must not be punished for a conversation the user is visibly
 *     driving.  A long assistant session takes ONE hold whose count never
 *     returns to zero (each new turn arrives inside the grace window), so
 *     `idleHeldSince` keeps its first stamp for the whole conversation.
 *     Measuring from that alone would fade the screen mid-stream after five
 *     minutes of active back-and-forth — the exact bug #29 exists to fix —
 *     and then re-fade every 60s afterwards, because nothing clears the stale
 *     stamp.  Every genuine interaction therefore moves `lastActivityAt`.
 *
 * The ceiling still cannot be suppressed forever: nothing but a real touch
 * moves `lastActivityAt`, so an untouched tablet always fades within five
 * minutes no matter how wedged the hold is.  An idle timer that can be
 * suppressed forever is a strictly worse bug than the one #29 set out to fix.
 *
 * Exported so the tests that assert the ceiling import the real value instead
 * of hand-copying it — a copy cannot detect drift in the thing it copies.
 */
export const MAX_HOLD_MS = 5 * 60_000;

export function useIdleTimer(enabled: boolean, idleMs = IDLE_MS): void {
  useEffect(() => {
    // No wall-tablet mode → no auto-idle.  Don't install any listeners or
    // timers; the device just stays interactive.
    if (!enabled) return undefined;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastMove = 0;
    /**
     * Wall-clock stamp of the last genuine interaction — the deadman's other
     * baseline (see MAX_HOLD_MS).  `Date.now()`, deliberately, because it has
     * to be comparable with `idleHeldSince`; `lastMove` above is a separate
     * `performance.now()` clock that exists only to throttle mousemove.
     */
    let lastActivityAt = Date.now();
    let paused = document.visibilityState === 'hidden';

    const schedule = (ms: number = idleMs): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      if (paused) return;
      timer = setTimeout(fire, ms);
    };

    function fire(): void {
      timer = null;
      const s = useDashboard.getState();

      if (s.idleHolds > 0) {
        // The ceiling runs from whichever came last: the 0→1 hold edge, or the
        // user's most recent real interaction.  A conversation the user is
        // actively driving therefore never trips it; only silence does.
        // `?? now` collapses the "no stamp yet" case to a zero-length hold
        // without a second branch: max(now, lastActivityAt) === now, so
        // heldFor === 0 and the timer simply defers.
        const now = Date.now();
        const heldFor = now - Math.max(s.idleHeldSince ?? now, lastActivityAt);
        if (heldFor <= MAX_HOLD_MS) {
          // Defer — something on screen is still worth reading.  Re-arm short
          // so the ceiling below is checked again soon.
          schedule(HOLD_RECHECK_MS);
          return;
        }
        // Ceiling breached: fall through and fade anyway.
      }

      s.goIdle();
      // Re-arm even after firing.  `goIdle()` is idempotent, and this keeps
      // the invariant "the fire path always leaves a live timer behind"
      // true on every branch — including the deadman one, where the hold is
      // still stuck and we want the screen to keep returning to the ambient
      // hub if the user wakes it.
      schedule();
    }

    const onActivity = (e: Event): void => {
      if (e.type === 'mousemove') {
        const now = performance.now();
        if (now - lastMove < MOUSEMOVE_THROTTLE_MS) return;
        lastMove = now;
      }
      // After the throttle guard, so a swipe's synthetic mousemove storm
      // costs one stamp per second rather than one per frame.
      lastActivityAt = Date.now();
      schedule();
    };

    const onVis = (): void => {
      paused = document.visibilityState === 'hidden';
      if (paused) {
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
      } else {
        // Coming back to a foregrounded tab is an interaction: the user just
        // returned to the screen, so the deadman restarts with the 60s window.
        lastActivityAt = Date.now();
        schedule();
      }
    };

    schedule();

    const types = [
      'pointerdown',
      'touchstart',
      'keydown',
      'click',
      'mousemove',
    ] as const;
    for (const t of types) {
      window.addEventListener(t, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVis);

    // Release edge: the last holder letting go grants a fresh, whole idleMs
    // window rather than the remainder of a HOLD_RECHECK_MS tick.
    const unsubscribe = useDashboard.subscribe((s, prev) => {
      if (prev.idleHolds > 0 && s.idleHolds === 0) schedule();
    });

    return () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      for (const t of types) {
        window.removeEventListener(t, onActivity);
      }
      document.removeEventListener('visibilitychange', onVis);
      unsubscribe();
    };
  }, [enabled, idleMs]);
}
